import {
  ApolloClient,
  ApolloLink,
  DefaultOptions,
  from,
  HttpLink,
  InMemoryCache,
  NextLink,
  Operation,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { sha256 } from 'crypto-hash';
import { useAuth } from 'react-oidc-context';
import { toast } from 'react-toastify';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import useCountryContext, { CountryContext } from './useCountryContext';

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors || networkError) {
    toast.error('Unexpected error', {
      position: 'top-center',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
    });
    graphQLErrors?.forEach(({ message }) => {
      // TODO: Is console.log still required ?
      // eslint-disable-next-line no-console
      console.log(message);
    });
    // TODO: Is console.log still required ?
    // eslint-disable-next-line no-console
    console.log(networkError);
  }
});

const authMiddleware = (
  authToken: string | undefined,
  countryContext: CountryContext | undefined,
) =>
  new ApolloLink((operation: Operation, forward: NextLink) => {
    if (authToken) {
      operation.setContext({
        headers: {
          authorization: `Bearer ${authToken}`,
          'X-Tenant-Country': countryContext,
        },
      });
    }
    return forward(operation);
  });

const cache = new InMemoryCache({});

const persistedQueriesLink = createPersistedQueryLink({ sha256 });

const httpLink = new HttpLink({
  uri: `${process.env.REACT_APP_BACKEND_URL}/graphql`,
});

const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'ignore',
  },
  query: {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  },
};

const useApolloClient = () => {
  const auth = useAuth();
  const { countryContext } = useCountryContext(false);
  return new ApolloClient({
    link: from([
      authMiddleware(auth.user?.access_token, countryContext),
      errorLink,
      persistedQueriesLink,
      httpLink,
    ]),
    cache,
    defaultOptions,
  });
};

export default useApolloClient;
